import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Upload, Trash2, Loader2, Image, Plus, LayoutTemplate, Images, AlertCircle } from "lucide-react";

const MAX_SIZE_BYTES = 10 * 1024 * 1024; // 10 MB
const ALLOWED_TYPES = ["image/jpeg", "image/png", "image/webp", "image/avif"];

interface ImageUploaderProps {
  heroUrl: string | null;
  galleryUrls: string[];
  onHeroUpload: (file: File) => Promise<void>;
  onHeroDelete: () => Promise<void>;
  onGalleryAdd: (file: File) => Promise<void>;
  onGalleryDelete: (index: number) => Promise<void>;
}

export function ImageUploader({
  heroUrl,
  galleryUrls,
  onHeroUpload,
  onHeroDelete,
  onGalleryAdd,
  onGalleryDelete,
}: ImageUploaderProps) {
  const { t: tRaw } = useTranslation();
  const t = (key: string) => tRaw(`themes.${key}`);
  const heroInputRef = useRef<HTMLInputElement>(null);
  const galleryInputRef = useRef<HTMLInputElement>(null);
  const [heroUploading, setHeroUploading] = useState(false);
  const [heroDeleting, setHeroDeleting] = useState(false);
  const [galleryUploading, setGalleryUploading] = useState(false);
  const [deletingIndex, setDeletingIndex] = useState<number | null>(null);
  const [heroError, setHeroError] = useState<string | null>(null);
  const [galleryError, setGalleryError] = useState<string | null>(null);

  function validateFile(file: File): string | null {
    if (!ALLOWED_TYPES.includes(file.type)) return t("uploadInvalidType");
    if (file.size > MAX_SIZE_BYTES) return t("uploadTooLarge");
    return null;
  }

  async function handleHeroChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";
    setHeroError(null);
    const err = validateFile(file);
    if (err) { setHeroError(err); return; }
    setHeroUploading(true);
    try { await onHeroUpload(file); } finally { setHeroUploading(false); }
  }

  async function handleHeroDelete() {
    if (!window.confirm(tRaw("themes.deleteImageConfirm"))) return;
    setHeroDeleting(true);
    try { await onHeroDelete(); } finally { setHeroDeleting(false); }
  }

  async function handleGalleryChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";
    setGalleryError(null);
    const err = validateFile(file);
    if (err) { setGalleryError(err); return; }
    setGalleryUploading(true);
    try { await onGalleryAdd(file); } finally { setGalleryUploading(false); }
  }

  async function handleGalleryDelete(index: number) {
    if (!window.confirm(tRaw("themes.deleteImageConfirm"))) return;
    setDeletingIndex(index);
    try { await onGalleryDelete(index); } finally { setDeletingIndex(null); }
  }

  return (
    <div className="space-y-6">
      {/* ── Hero Image ─────────────────────────────────── */}
      <div className="rounded-2xl border border-border overflow-hidden">
        <div className="flex items-start gap-3 bg-muted/50 px-4 py-4 border-b border-border">
          <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-primary/10">
            <LayoutTemplate className="h-4 w-4 text-primary" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold">{t("heroImage")}</p>
            <p className="mt-0.5 text-xs text-muted-foreground leading-relaxed">
              {t("heroImageDesc")}
            </p>
          </div>
        </div>

        <div className="p-4 space-y-3">
          <input
            ref={heroInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/avif"
            className="hidden"
            onChange={handleHeroChange}
          />
          {/* Preview */}
          <div className="overflow-hidden rounded-xl border border-border bg-muted h-44">
            {heroUrl ? (
              <img src={heroUrl} alt="" className="h-full w-full object-cover" />
            ) : (
              <div className="flex h-full flex-col items-center justify-center gap-2 text-muted-foreground">
                <Image className="h-10 w-10 opacity-30" />
                <p className="text-xs">{t("noHero")}</p>
              </div>
            )}
          </div>

          {/* Action buttons — always visible */}
          <div className="flex gap-2">
            <button
              onClick={() => heroInputRef.current?.click()}
              disabled={heroUploading}
              className="flex flex-1 items-center justify-center gap-1.5 rounded-xl border border-border py-2 text-sm font-medium hover:bg-muted transition-colors cursor-pointer disabled:opacity-50"
            >
              {heroUploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
              {heroUrl ? t("changeImage") : t("addImage")}
            </button>
            {heroUrl && (
              <button
                onClick={handleHeroDelete}
                disabled={heroDeleting}
                className="flex items-center gap-1.5 rounded-xl border border-red-200 dark:border-red-900 bg-red-50 dark:bg-red-950/30 px-4 py-2 text-sm font-medium text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-950/50 transition-colors cursor-pointer disabled:opacity-50"
              >
                {heroDeleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                {tRaw("common.delete")}
              </button>
            )}
          </div>

          {/* Inline error */}
          {heroError && (
            <div className="flex items-start gap-2 rounded-xl bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900 px-3 py-2.5 text-xs text-red-700 dark:text-red-400">
              <AlertCircle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
              <span>{heroError}</span>
            </div>
          )}
        </div>
      </div>

      {/* ── Gallery Images ──────────────────────────────── */}
      <div className="rounded-2xl border border-border overflow-hidden">
        <div className="flex items-start gap-3 bg-muted/50 px-4 py-4 border-b border-border">
          <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-primary/10">
            <Images className="h-4 w-4 text-primary" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center justify-between">
              <p className="text-sm font-semibold">{t("gallery")}</p>
              <span className="text-xs text-muted-foreground tabular-nums">
                {galleryUrls.length} / 5
              </span>
            </div>
            <p className="mt-0.5 text-xs text-muted-foreground leading-relaxed">
              {t("galleryDesc")}
            </p>
          </div>
        </div>

        <div className="p-4 space-y-3">
          <input
            ref={galleryInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/avif"
            className="hidden"
            onChange={handleGalleryChange}
          />
          <div className="grid grid-cols-3 gap-2 sm:grid-cols-5">
            {galleryUrls.map((url, i) => (
              <div key={i} className="relative overflow-hidden rounded-xl border border-border aspect-square group">
                <img src={url} alt="" className="h-full w-full object-cover" />
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 bg-black/40 transition-opacity">
                  <button
                    onClick={() => handleGalleryDelete(i)}
                    disabled={deletingIndex === i}
                    className="rounded-full bg-red-500/90 p-1.5 cursor-pointer shadow-sm"
                  >
                    {deletingIndex === i
                      ? <Loader2 className="h-3 w-3 animate-spin text-white" />
                      : <Trash2 className="h-3 w-3 text-white" />
                    }
                  </button>
                </div>
              </div>
            ))}
            {galleryUrls.length < 5 && (
              <button
                onClick={() => galleryInputRef.current?.click()}
                disabled={galleryUploading}
                className="aspect-square rounded-xl border-2 border-dashed border-border hover:border-primary/40 hover:bg-primary/5 flex flex-col items-center justify-center gap-1 transition-colors cursor-pointer disabled:opacity-50"
              >
                {galleryUploading
                  ? <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                  : <Plus className="h-5 w-5 text-muted-foreground" />
                }
                {!galleryUploading && (
                  <span className="text-[10px] text-muted-foreground">{t("addImage")}</span>
                )}
              </button>
            )}
          </div>

          {/* Inline error */}
          {galleryError && (
            <div className="flex items-start gap-2 rounded-xl bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900 px-3 py-2.5 text-xs text-red-700 dark:text-red-400">
              <AlertCircle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
              <span>{galleryError}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
